import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import BuildIcon from '@mui/icons-material/Build';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LabelIcon from '@mui/icons-material/Label';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TuneIcon from '@mui/icons-material/Tune';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute } from '@tanstack/react-router';
import { useAtom } from 'jotai';
import { useState } from 'react';

import { ProjectName } from '#components/project-name/project-name.js';
import { useTRPC } from '#src/trpc.js';
import { Can, useAbilityContext, useAbilityWithoutOverrideContext } from '#state/contexts/ability-context.js';
import { useNavigationLinkParams } from '#utils/use-navigation-link-params.js';
import { showDevToolsAtom } from '../../show-dev-tools-atom.js';
import { UserControl } from '../user-control/user-control.js';
import { ViewAsUserToggle } from '../view-as-user-toggle.js';
import { OrganizationSwitcher } from './organization-switcher.js';
import { SidebarLink } from './sidebar-link.js';

export function SidebarContent() {
	const abilityWithoutOverride = useAbilityWithoutOverrideContext();
	const regularAbility = useAbilityContext();
	const { orgName: navigationOrgName, projectId, revisionId } = useNavigationLinkParams();
	const trpc = useTRPC();
	const matchRoute = useMatchRoute();
	const isOnProjectList = matchRoute({ to: '/orgs/$orgName/projects', fuzzy: false }) !== false;

	const [, setShowDevtools] = useAtom(showDevToolsAtom);
	const [isDevToolsMenuOpen, setIsDevToolsMenuOpen] = useState<{ anchorEl: HTMLElement } | null>(null);

	const { data: projectPreviewData } = useQuery(
		trpc.project.getPreview.queryOptions({ id: projectId ?? '' }, { enabled: !!projectId, throwOnError: false }),
	);
	const { data: currentUser } = useQuery(
		trpc.user.getCurrent.queryOptions(undefined, {
			throwOnError: false,
		}),
	);
	const projectName = projectPreviewData?.projectPreview.name;
	// Purposefully not using the `<Can>` component here, because that component uses the regular ability object
	// (with the "view as user" override applied), and the "view as user" toggle itself is in the dev tools menu --
	// switching it on would hide the dev tools menu, which would make it impossible to turn it off again.
	const canSeeDevTools = abilityWithoutOverride.can('manage', 'devTools');
	const canSeePackageSection =
		regularAbility.can('manage', 'package') ||
		regularAbility.can('manage', 'manufacturer') ||
		regularAbility.can('manage', 'tag');
	const canSeeToolsSection = regularAbility.can('manage', 'wizard');

	const projectLinkParams =
		projectId && revisionId && navigationOrgName ?
			{
				orgName: navigationOrgName,
				projectId,
				revisionId,
			}
		:	null;

	const isStaff = currentUser?.auth.isStaff;

	return (
		<div className='flex min-h-0 min-w-0 grow flex-col'>
			{/* Scrollable nav content */}
			<div className='flex min-h-0 min-w-0 grow flex-col gap-2 overflow-y-auto p-1'>
				<OrganizationSwitcher className='mt-0.5' />

				<div className='flex flex-col gap-0.5'>
					<Can I='view' a='project'>
						{navigationOrgName ?
							<SidebarLink
								to='/orgs/$orgName/projects'
								params={{ orgName: navigationOrgName }}
								activeOptions={{ exact: true }}
								icon={<FolderOpenIcon />}
								label='All projects'
								active={isOnProjectList}
							/>
						:	<SidebarLink to='/' icon={<FolderOpenIcon />} label='All projects' active={isOnProjectList} />}
					</Can>

					<Can I='manage' a='organization'>
						{navigationOrgName && (
							<SidebarLink
								to='/orgs/$orgName/settings'
								params={{ orgName: navigationOrgName }}
								icon={<SettingsIcon />}
								label='Org settings'
							/>
						)}
					</Can>
				</div>

				{/* Current project links */}
				{projectLinkParams && (
					<>
						<div className='mx-1 h-px self-stretch bg-gray-a3' />

						<div className='flex min-w-0 flex-col gap-0.5'>
							<ProjectName
								isDemo={projectPreviewData?.projectPreview.isDemo ?? false}
								className='min-w-0 px-2 py-1.5 text-xs font-semibold tracking-wide text-gray-a9 uppercase'
							>
								<span className='truncate min-w-0 grow basis-0'>
									{projectName ?? <Skeleton className='inline-block w-20' />}
								</span>
							</ProjectName>

							<Can I='manage' a='project'>
								<SidebarLink
									to='/orgs/$orgName/projects/$projectId/overview'
									params={projectLinkParams}
									icon={<SpaceDashboardIcon />}
									label='Overview'
								/>
							</Can>
							<Can I='manage' a='moduleResolution'>
								<SidebarLink
									to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/resolution-editor'
									params={projectLinkParams}
									icon={<TuneIcon />}
									label='Resolution editor'
								/>
							</Can>
							<Can I='view' a='analysisResult'>
								<SidebarLink
									to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/results'
									params={projectLinkParams}
									icon={<BarChartIcon />}
									label='Results'
								/>
							</Can>
							<Can I='manage' a='legacyFinding'>
								<SidebarLink
									to='/orgs/$orgName/projects/$projectId/revisions/$revisionId/ai-findings'
									params={projectLinkParams}
									icon={<QueryStatsIcon />}
									label='AI findings'
								/>
							</Can>
						</div>
					</>
				)}

				{canSeePackageSection && (
					<>
						<div className='mx-1 h-px self-stretch bg-gray-a3' />

						<div className='flex flex-col gap-0.5'>
							<p className='px-2 py-1.5 text-xs font-semibold tracking-wide text-gray-a9 uppercase'>Packages</p>

							<Can I='manage' a='package'>
								<SidebarLink to='/package-store' icon={<StorefrontIcon />} label='Package Store' />
							</Can>
							<Can I='manage' a='manufacturer'>
								<SidebarLink to='/manufacturers' icon={<BuildIcon />} label='Manufacturer index' />
							</Can>
							<Can I='manage' a='tag'>
								<SidebarLink to='/package-tags' icon={<LabelIcon />} label='Package tags' />
							</Can>
						</div>
					</>
				)}

				{canSeeToolsSection && (
					<>
						<div className='mx-1 h-px self-stretch bg-gray-a3' />

						<div className='flex flex-col gap-0.5'>
							<p className='px-2 py-1.5 text-xs font-semibold tracking-wide text-gray-a9 uppercase'>Tools</p>
							<Can I='manage' a='wizard'>
								<SidebarLink to='/wizard' icon={<AutoAwesomeIcon />} label='Wizard' />
							</Can>
							{isStaff && (
								<SidebarLink to='/tools/admin/pipeline-ops' icon={<MonitorHeartIcon />} label='Pipeline ops' />
							)}
						</div>
					</>
				)}
			</div>

			{/* Fixed bottom: user + dev tools */}
			<div className='mt-2 flex items-center justify-between gap-3 border-t border-gray-a3 px-4 py-2'>
				<UserControl />
				{canSeeDevTools && (
					<>
						<IconButton
							size='small'
							onClick={(event) => setIsDevToolsMenuOpen({ anchorEl: event.currentTarget })}
							className='text-gray-a9'
						>
							<MoreVertIcon />
						</IconButton>
						<Menu
							anchorEl={isDevToolsMenuOpen?.anchorEl}
							open={isDevToolsMenuOpen !== null}
							onClose={() => setIsDevToolsMenuOpen(null)}
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						>
							<MenuItem
								onClick={() => {
									setShowDevtools((val) => (val === 'tanstackRouter' ? null : 'tanstackRouter'));
									setIsDevToolsMenuOpen(null);
								}}
							>
								<p>TanStack Router Devtools</p>
							</MenuItem>
							<MenuItem
								onClick={() => {
									setShowDevtools((val) => (val === 'tanstackQuery' ? null : 'tanstackQuery'));
									setIsDevToolsMenuOpen(null);
								}}
							>
								<p>TanStack Query Devtools</p>
							</MenuItem>
							<div className='flex p-1'>
								<ViewAsUserToggle />
							</div>
						</Menu>
					</>
				)}
			</div>
		</div>
	);
}
